import { appConfig, isUsingMocks, getDqxOverride } from "@/config/app";
import { trackEvent, trackException } from "@/services/telemetry";
import type {
  TargetSchema,
  UploadConfirmation,
  KnownMapping,
  HistoricalFile,
  SchemaField,
  MappingRow,
  ParsedWorksheet,
  DqxValidationResult,
  DqxCellError,
  DqxErrorCategory,
  ClassificationResult,
  FieldRequirement,
} from "@/types";
import {
  transformSchemaFields,
  transformProcessResponse,
  transformKnownMappings,
  transformFiles,
  transformUploadResponse,
  transformFieldRequirements,
} from "@/services/transforms";
import type {
  ApiSchemaField,
  ApiKnownMapping,
  ApiFile,
  ApiFieldRequirement,
  ApiProcessResponse,
  ApiUploadResponse,
} from "@/services/transforms";
import {
  mockTargetSchemas,
  mockHistoricalFiles,
  mockKnownMappings,
  simulateUpload,
} from "@/mocks/data";
import { LLOYDS_52_SCHEMA } from "@/mocks/mappingData";
import { generateDynamicMappings } from "@/mocks/mockProcessing";
import { parseFile } from "@/services/fileParser";
import { runMockDqxValidation } from "@/mocks/mockDqx";

// ── Token Provider ─────────────────────────────────────────

type GetTokenFn = () => Promise<string | null>;
let getToken: GetTokenFn = async () => null;

export function registerTokenProvider(fn: GetTokenFn): void {
  getToken = fn;
}

// ── Core Fetch Helpers ─────────────────────────────────────

async function authHeaders(isMultipart = false): Promise<HeadersInit> {
  const token = await getToken();
  const headers: HeadersInit = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (!isMultipart) headers["Content-Type"] = "application/json";
  return headers;
}

interface ApiResponseError extends Error {
  statusCode: number;
  code: string;
  /** Populated for some fetches (e.g. contract-rules) so the UI can show the HTTP body on failure. */
  rawResponseText?: string;
}

/** FastAPI uses `detail` (string or validation array); some routes use `message`. */
function formatApiErrorBody(body: unknown): string {
  if (body === null || typeof body !== "object") return String(body ?? "");
  const r = body as Record<string, unknown>;
  if (typeof r.message === "string" && r.message) return r.message;
  const d = r.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .join("; ");
  }
  try {
    return JSON.stringify(body);
  } catch {
    return "Unknown error";
  }
}

export interface DqxTriggerResult {
  runId: number;
  dqxJobId: string;
  message?: string | null;
}

export interface DqxRunStatusResult {
  runId: number;
  lifeCycleState: string;
  resultState: string | null;
}

export interface ApplyMappingsResult {
  rows_written: number;
  dqx_triggered?: boolean;
  dqx_run_id?: number | null;
  dqx_job_id?: string | null;
  dqx_trigger_message?: string | null;
}

export interface ManualCleanseRevalidateResult {
  audit_entries_written: number;
  rows_rewritten: number;
  cells_applied: number;
  dqx_triggered: boolean;
  dqx_run_id: number | null;
  dqx_job_id: string | null;
  dqx_trigger_message: string | null;
  mapping_run_id: string | null;
}

export interface DqxQuarantineResults {
  dqx_job_id: string;
  // Server-resolved from klyra_files; null when the file row has gone.
  file_id?: string | null;
  schema_id?: string | null;
  quarantine_table: Record<string, unknown>[];
  quarantine_summary_table: Record<string, unknown>[];
}

/** Parsed explicit DQX rules from GET /dqx/contract-rules/{path} */
export interface DqxContractRulesResponse {
  contract_name: string;
  contract_path: string;
  contract_rules: Record<string, unknown>[];
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${appConfig.api.baseUrl}${path}`, {
    ...init,
    headers: { ...headers, ...init?.headers },
    signal: init?.signal ?? AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.message || `API error: ${res.status}`);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = body.error;
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: body.error ?? "unknown",
      method: init?.method ?? "GET",
    });
    throw error;
  }

  return res.json();
}

async function apiMultipartFetch<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal
): Promise<T> {
  const headers = await authHeaders(true);
  const res = await fetch(`${appConfig.api.baseUrl}${path}`, {
    method: "POST",
    headers,
    body: formData,
    signal: signal ?? AbortSignal.timeout(appConfig.api.timeout),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.message || `Upload failed: ${res.status}`);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code = body.error;
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: body.error ?? "unknown",
      method: "POST_MULTIPART",
    });
    throw error;
  }

  return res.json();
}

// ── Feature flag ────────────────────────────────────────────
// Evaluated per-call so ?mocks=true URL override works at runtime
const shouldUseMocks = isUsingMocks;

// ── API Methods ────────────────────────────────────────────

export async function fetchTargetSchemas(): Promise<TargetSchema[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(mockTargetSchemas), 200));
  }
  const data = await apiFetch<{ id: string; name: string }[]>("/schemas");
  return data.map((s) => ({ id: s.id, name: s.name, version: "" }));
}

export async function fetchSchemaFields(
  schemaId?: string,
  fieldCategory?: string,
  reportType?: string
): Promise<SchemaField[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(LLOYDS_52_SCHEMA), 200));
  }
  const params = new URLSearchParams();
  if (schemaId) params.set("schema_id", schemaId);
  if (fieldCategory) params.set("field_category", fieldCategory);
  // When provided, the backend returns is_mandatory scoped to this report_type
  // so the mapping UI only marks fields mandatory for the selected contract.
  if (reportType) params.set("report_type", reportType);
  const qs = params.toString();
  const data = await apiFetch<ApiSchemaField[]>(`/schema-fields${qs ? `?${qs}` : ""}`);
  trackEvent("api_schema_fields_fetched", {
    count: data.length,
    schemaId: schemaId ?? "all",
    fieldCategory: fieldCategory ?? "all",
  });
  return transformSchemaFields(data);
}

/** @deprecated Use fetchSchemaFields */
export const fetchTargetColumns = fetchSchemaFields;

export async function uploadFile(file: File, schemaId: string): Promise<UploadConfirmation> {
  if (shouldUseMocks()) {
    const result = await simulateUpload();
    trackEvent("FileUpload.Succeeded", {
      documentId: result.fileId,
      fileName: file.name,
      fileSize: file.size,
      schemaId,
      mock: true,
    });
    return {
      documentId: result.fileId,
      fileName: file.name,
      uploadedBy: "Mock User",
      uploadedAt: new Date().toISOString(),
      targetSchema: schemaId,
      status: "success",
      fileSize: file.size,
      mimeType: file.type || undefined,
    };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("schemaId", schemaId);

  const raw = await apiMultipartFetch<ApiUploadResponse>("/files/upload", formData);
  return transformUploadResponse(raw);
}

export async function previewFile(file: File): Promise<ParsedWorksheet[]> {
  if (shouldUseMocks()) {
    // Fall back to client-side parsing in mock mode
    return parseFile(file);
  }

  const formData = new FormData();
  formData.append("file", file);

  const raw = await apiMultipartFetch<{
    worksheets: {
      name: string;
      headers: string[];
      rows: Record<string, string>[];
      total_rows: number;
      is_metadata: boolean;
    }[];
  }>("/files/preview", formData);

  return raw.worksheets.map((ws) => ({
    name: ws.name,
    headers: ws.headers,
    rows: ws.rows,
    totalRows: ws.total_rows,
    isMetadata: ws.is_metadata,
  }));
}

export interface ProcessResult {
  rows: MappingRow[];
  sourceColumns: string[];
  duplicateColumns: string[];
  processingTimeMs: number;
}

/**
 * Call the Intelligent File Classification endpoint in parallel with the
 * preview upload. Returns the suggested report_type plus the full list of
 * allowed_report_types so the Preview dialog can render its File Type
 * dropdown options directly from this response.
 *
 * In mock mode we short-circuit to `unknown` with the canonical Lloyd's
 * report_types — the real cascade depends on DB-backed schema data that
 * the frontend mocks don't simulate.
 */
export async function classifyFile(
  file: File,
  schemaId: string,
  signal?: AbortSignal
): Promise<ClassificationResult> {
  if (shouldUseMocks()) {
    await new Promise((r) => setTimeout(r, 400 + Math.random() * 500));
    return {
      detectedType: "unknown",
      reason: "Mock mode — classification skipped",
      stepUsed: "none",
      score: null,
      allowedReportTypes: ["claims", "risk_premiums"],
    };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("schema_id", schemaId);

  const raw = await apiMultipartFetch<{
    detected_type: string;
    reason: string;
    step_used: "filename" | "none";
    score: number | null;
    allowed_report_types: string[];
  }>("/files/classify", formData, signal);

  trackEvent("Classify.Completed", {
    schemaId,
    detectedType: raw.detected_type,
    stepUsed: raw.step_used,
    fileName: file.name,
  });

  return {
    detectedType: raw.detected_type,
    reason: raw.reason,
    stepUsed: raw.step_used,
    score: raw.score,
    allowedReportTypes: raw.allowed_report_types,
  };
}

export async function processFile(
  file: File,
  fileId: string,
  schemaFields: SchemaField[],
  knownMappings: KnownMapping[],
  reportType?: string | null
): Promise<ProcessResult> {
  if (shouldUseMocks()) {
    // Parse the actual uploaded file to extract real source column headers
    const worksheets = await parseFile(file);
    const headerSet = new Set<string>();
    for (const ws of worksheets) {
      if (ws.isMetadata) continue;
      for (const h of ws.headers) headerSet.add(h);
    }
    const realSourceColumns = Array.from(headerSet);

    // Generate dynamic mappings using real headers against the schema
    const mockResult = generateDynamicMappings(realSourceColumns, schemaFields);

    trackEvent("Processing.MockCompleted", {
      fileName: file.name,
      sourceColumnCount: realSourceColumns.length,
      mappingCount: mockResult.rows.length,
      processingTimeMs: mockResult.processingTimeMs,
    });

    // Simulate realistic network + processing latency
    await new Promise((r) => setTimeout(r, 800 + Math.random() * 700));

    return mockResult;
  }

  const payload = {
    file_id: fileId,
    file_name: file.name,
    known_mappings: knownMappings.map((km) => ({
      mapping_id: km.mappingId,
      source_field: km.sourceField,
      target_field: km.targetFieldId,
      schema_id: km.schemaId,
      confidence: km.confidence ?? 1.0,
    })),
    schema_fields: schemaFields.map((f) => ({
      schema_id: f.schemaId ?? "",
      field_id: f.id,
      field_name: f.fieldName,
      field_category: f.fieldCategory,
      data_type: f.dataType,
      max_length: f.maxLength ?? null,
      definition: f.definition,
      is_mandatory: f.mandatory,
    })),
    tenant_config: {
      tenant_id: "dev-tenant-001",
      catalog_name: "klyra_bdx",
      provider: "default",
    },
    // Confirmed report_type from the Preview screen's File Type dropdown.
    // The backend uses this to filter schema_fields to the correct
    // contract (claims vs risk_premiums) before mapping runs. Null means
    // no filter — the classifier was unsure and the user hasn't been
    // prompted yet (legacy flows / non-classified schemas).
    report_type: reportType ?? null,
  };

  const formData = new FormData();
  formData.append("file", file);
  formData.append("payload", JSON.stringify(payload));

  const response = await apiMultipartFetch<ApiProcessResponse>("/process", formData);
  const { rows, sourceColumns, duplicateColumns } = transformProcessResponse(
    response,
    schemaFields
  );

  return { rows, sourceColumns, duplicateColumns, processingTimeMs: response.processing_time_ms };
}

export async function fetchKnownMappings(schemaId?: string): Promise<KnownMapping[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(mockKnownMappings), 300));
  }
  const params = schemaId ? `?schema_id=${encodeURIComponent(schemaId)}` : "";
  const data = await apiFetch<ApiKnownMapping[]>(`/known-mappings${params}`);
  trackEvent("api_known_mappings_fetched", { count: data.length, schemaId: schemaId ?? "all" });
  return transformKnownMappings(data);
}

/**
 * Fetches all rows from klyra_field_requirements via GET /api/v1/field-requirements.
 * Used by KnownMappingsPage to derive per-schema report_type dropdown options and
 * to filter mappings by (schema_id, field_id) when a report_type is selected.
 * Returns [] in mock mode — the dropdown will be empty and filtering is a no-op.
 */
export async function fetchFieldRequirements(): Promise<FieldRequirement[]> {
  if (shouldUseMocks()) {
    return [];
  }
  const data = await apiFetch<ApiFieldRequirement[]>("/field-requirements");
  trackEvent("api_field_requirements_fetched", { count: data.length });
  return transformFieldRequirements(data);
}

export async function createKnownMappings(
  mappings: {
    schemaId: string;
    sourceField: string;
    targetField: string;
    approvedBy?: string;
  }[]
): Promise<void> {
  if (mappings.length === 0) return;
  if (shouldUseMocks()) return;
  await apiFetch("/known-mappings/batch", {
    method: "POST",
    body: JSON.stringify({
      mappings: mappings.map((m) => ({
        schema_id: m.schemaId,
        source_field: m.sourceField,
        target_field: m.targetField,
        approved_by: m.approvedBy ?? "",
      })),
    }),
  });
  trackEvent("api_known_mappings_created", {
    count: mappings.length,
    schemaId: mappings[0]?.schemaId ?? "unknown",
  });
}

/** Convenience wrapper for single-item callers. */
export async function createKnownMapping(mapping: {
  schemaId: string;
  sourceField: string;
  targetField: string;
  approvedBy?: string;
}): Promise<void> {
  return createKnownMappings([mapping]);
}

export async function deleteKnownMappings(mappingIds: string[]): Promise<void> {
  if (mappingIds.length === 0) return;
  if (shouldUseMocks()) return;
  await apiFetch("/known-mappings", {
    method: "DELETE",
    body: JSON.stringify({ mapping_ids: mappingIds }),
  });
  trackEvent("api_known_mappings_deleted", { count: mappingIds.length });
}

export async function deleteKnownMapping(mappingId: string): Promise<void> {
  if (shouldUseMocks()) return;
  await apiFetch(`/known-mappings/${encodeURIComponent(mappingId)}`, {
    method: "DELETE",
  });
  trackEvent("api_known_mappings_deleted", { count: 1 });
}

export async function applyMappings(params: {
  fileId: string;
  schemaId: string;
  reportType?: string | null;
  mappings: { source_col: string; target_col: string }[];
}): Promise<ApplyMappingsResult> {
  if (shouldUseMocks()) {
    return {
      rows_written: 0,
      dqx_triggered: true,
      dqx_run_id: Date.now(),
      dqx_job_id: `mock-dqx-${Date.now()}`,
      dqx_trigger_message: "Mock DQX trigger accepted",
    };
  }
  const reportType = params.reportType?.trim();
  const result = await apiFetch<ApplyMappingsResult>("/apply-mappings", {
    method: "POST",
    body: JSON.stringify({
      file_id: params.fileId,
      schema_id: params.schemaId,
      ...(reportType ? { report_type: reportType } : {}),
      mappings: params.mappings,
    }),
  });
  trackEvent("api_apply_mappings", {
    fileId: params.fileId,
    schemaId: params.schemaId,
    mappingCount: params.mappings.length,
    rowsWritten: result.rows_written,
    dqxRunId: result.dqx_run_id ?? undefined,
    dqxTriggered: result.dqx_triggered ?? undefined,
  });
  return result;
}

export async function revalidateManualCleanse(params: {
  fileId: string;
  schemaId: string;
  edits: {
    cell_key: string;
    row_index: number;
    column_index: number;
    source_column_name: string;
    old_value: string;
    new_value: string;
    user_id: string;
    user_email: string;
    timestamp: string;
  }[];
}): Promise<ManualCleanseRevalidateResult> {
  if (shouldUseMocks()) {
    return {
      audit_entries_written: params.edits.length,
      rows_rewritten: params.edits.length,
      cells_applied: params.edits.length,
      dqx_triggered: true,
      dqx_run_id: Date.now(),
      dqx_job_id: `mock-dqx-${Date.now()}`,
      dqx_trigger_message: "Mock revalidate accepted",
      mapping_run_id: `mock-run-${Date.now()}`,
    };
  }
  const result = await apiFetch<ManualCleanseRevalidateResult>("/manual-cleanse/revalidate", {
    method: "POST",
    body: JSON.stringify({
      file_id: params.fileId,
      schema_id: params.schemaId,
      edits: params.edits,
    }),
  });
  trackEvent("api_manual_cleanse_revalidate", {
    fileId: params.fileId,
    editCount: params.edits.length,
    auditEntriesWritten: result.audit_entries_written,
    cellsApplied: result.cells_applied,
    dqxTriggered: result.dqx_triggered,
  });
  return result;
}

export async function fetchHistoricalFiles(): Promise<HistoricalFile[]> {
  if (shouldUseMocks()) {
    return new Promise((resolve) => setTimeout(() => resolve(mockHistoricalFiles), 300));
  }
  const data = await apiFetch<ApiFile[]>("/documents");
  trackEvent("api_files_fetched", { count: data.length });
  return transformFiles(data);
}

export async function fetchColumnMappings(_fileId: string): Promise<MappingRow[]> {
  return [];
}

export async function triggerDqxValidationJob(params: {
  fileId: string;
  schemaId?: string;
  fileName?: string;
  mappingCount?: number;
}): Promise<DqxTriggerResult> {
  if (shouldUseMocks()) {
    return {
      runId: Date.now(),
      dqxJobId: `mock-dqx-${Date.now()}`,
      message: "Mock DQX trigger accepted",
    };
  }

  const rawJobId = appConfig.dqx.databricksJobId?.trim();
  if (!rawJobId) {
    throw new Error("VITE_DATABRICKS_JOB_ID is not configured.");
  }

  const jobId = Number(rawJobId);
  if (!Number.isFinite(jobId)) {
    throw new Error(`VITE_DATABRICKS_JOB_ID must be numeric. Received "${rawJobId}".`);
  }

  const body = {
    job_id: jobId,
    job_parameters: {
      file_id: params.fileId,
      schema_id: params.schemaId ?? "",
      file_name: params.fileName ?? "",
      mapping_count: String(params.mappingCount ?? 0),
    },
  };

  const data = await apiFetch<{
    run_id: number;
    dqx_job_id: string;
    message?: string | null;
  }>("/dqx/trigger", {
    method: "POST",
    body: JSON.stringify(body),
  });

  trackEvent("Dqx.TriggerAccepted", {
    runId: data.run_id,
    dqxJobId: data.dqx_job_id,
    fileId: params.fileId,
    schemaId: params.schemaId ?? "unknown",
  });

  return {
    runId: data.run_id,
    dqxJobId: data.dqx_job_id,
    message: data.message,
  };
}

export async function fetchDqxValidationJobStatus(runId: number): Promise<DqxRunStatusResult> {
  if (shouldUseMocks()) {
    return {
      runId,
      lifeCycleState: "TERMINATED",
      resultState: "SUCCESS",
    };
  }

  const data = await apiFetch<{
    run_id: number;
    life_cycle_state: string;
    result_state: string | null;
  }>(`/dqx/status/${runId}`);

  trackEvent("Dqx.StatusChecked", {
    runId: data.run_id,
    lifeCycleState: data.life_cycle_state,
    resultState: data.result_state ?? "PENDING",
  });

  return {
    runId: data.run_id,
    lifeCycleState: data.life_cycle_state,
    resultState: data.result_state ?? null,
  };
}

export async function runDqxValidation(
  worksheets: ParsedWorksheet[],
  savedMappings: KnownMapping[],
  schemaFields?: SchemaField[]
): Promise<DqxValidationResult> {
  if (shouldUseMocks()) {
    const schema = schemaFields ?? LLOYDS_52_SCHEMA;
    // Simulate processing delay
    await new Promise((r) => setTimeout(r, 600 + Math.random() * 800));

    const dqxOverride = getDqxOverride();
    const result = runMockDqxValidation(worksheets, savedMappings, schema, dqxOverride);

    trackEvent("Dqx.MockCompleted", {
      hasErrors: result.hasErrors,
      totalErrorCells: result.totalErrorCells,
      dataFormatting: result.summary["Data Formatting"],
      summationLogic: result.summary["Summation Logic"],
      structureSyntax: result.summary["Structure & Syntax"],
      dqxOverride: dqxOverride ?? "none",
    });
    return result;
  }

  // Production: POST to sidecar DQX endpoint
  // TODO: implement when sidecar DQX route is ready
  const body = {
    worksheets: worksheets.map((ws) => ({
      name: ws.name,
      headers: ws.headers,
      row_count: ws.totalRows,
    })),
    mappings: savedMappings.map((m) => ({
      source_field: m.sourceField,
      target_field: m.targetField,
    })),
  };
  const data = await apiFetch<{
    has_errors: boolean;
    total_error_cells: number;
    summary: Record<string, number>;
    cell_errors: Record<
      string,
      {
        row_index: number;
        column_index: number;
        source_column_name: string;
        target_column_name: string;
        value: string;
        rules: string[];
        category: string;
      }
    >;
    error_report: {
      column_name: string;
      column_index: number;
      row_index: number;
      value: string;
      error: string;
      rule_type?: string;
    }[];
  }>("/dqx-validate", { method: "POST", body: JSON.stringify(body) });

  // Transform API response to DqxValidationResult
  const cellErrors = new Map<string, DqxCellError>();
  for (const [key, err] of Object.entries(data.cell_errors)) {
    cellErrors.set(key, {
      rowIndex: err.row_index,
      columnIndex: err.column_index,
      sourceColumnName: err.source_column_name,
      targetColumnName: err.target_column_name,
      value: err.value,
      rules: err.rules,
      ruleNames: err.rules,
      category: err.category as DqxErrorCategory,
    });
  }

  trackEvent("Dqx.Completed", {
    hasErrors: data.has_errors,
    totalErrorCells: data.total_error_cells,
  });

  return {
    hasErrors: data.has_errors,
    totalErrorCells: data.total_error_cells,
    summary: data.summary as DqxValidationResult["summary"],
    cellErrors: cellErrors as DqxValidationResult["cellErrors"],
    errorReport: data.error_report.map((r) => ({
      columnName: r.column_name,
      columnIndex: r.column_index,
      rowIndex: r.row_index,
      value: r.value,
      error: r.error,
      ruleType: r.rule_type,
    })),
  };
}

function normalizeDqxQuarantineResponse(raw: unknown): DqxQuarantineResults {
  if (raw === null || typeof raw !== "object") {
    return { dqx_job_id: "", quarantine_table: [], quarantine_summary_table: [] };
  }
  const r = raw as Record<string, unknown>;
  const table = r.quarantine_table ?? r.quarantineTable ?? (Array.isArray(raw) ? raw : undefined);
  const summary = r.quarantine_summary_table ?? r.quarantineSummaryTable;
  const jobId = String(r.dqx_job_id ?? r.dqxJobId ?? "");
  const fileId = r.file_id ?? r.fileId;
  const schemaId = r.schema_id ?? r.schemaId;
  return {
    dqx_job_id: jobId,
    file_id: typeof fileId === "string" ? fileId : null,
    schema_id: typeof schemaId === "string" ? schemaId : null,
    quarantine_table: Array.isArray(table) ? (table as Record<string, unknown>[]) : [],
    quarantine_summary_table: Array.isArray(summary) ? (summary as Record<string, unknown>[]) : [],
  };
}

export async function fetchDqxQuarantineResults(dqxJobId: string): Promise<DqxQuarantineResults> {
  if (shouldUseMocks()) {
    return {
      dqx_job_id: dqxJobId,
      quarantine_table: [],
      quarantine_summary_table: [],
    };
  }
  const raw = await apiFetch<unknown>(`/dqx/quarantine/${encodeURIComponent(dqxJobId)}`);
  return normalizeDqxQuarantineResponse(raw);
}

/**
 * Load explicit DQX rules extracted from the data contract YAML at the given path
 * (relative to the server-side contract base directory).
 */
export async function fetchDataContractRules(
  contractRelativePath: string
): Promise<DqxContractRulesResponse> {
  if (shouldUseMocks()) {
    return {
      contract_name: contractRelativePath,
      contract_path: "/mock/contract",
      contract_rules: [],
    };
  }
  const segments = contractRelativePath
    .split("/")
    .filter((s) => s.length > 0)
    .map((s) => encodeURIComponent(s))
    .join("/");
  return apiFetch<DqxContractRulesResponse>(`/dqx/contract-rules/${segments}`);
}

/**
 * Load DQX rules from the single `.yml`/`.yaml` under `{contract_base}/{schemaId}/` (server resolves path).
 */
export async function fetchDataContractRulesBySchema(
  schemaId: string
): Promise<DqxContractRulesResponse> {
  if (shouldUseMocks()) {
    return {
      contract_name: schemaId,
      contract_path: "/mock/contract",
      contract_rules: [],
    };
  }
  const q = new URLSearchParams({
    schema_id: schemaId,
  });
  const path = `/dqx/contract-rules/by-schema?${q.toString()}`;

  const headers = await authHeaders();
  const res = await fetch(`${appConfig.api.baseUrl}${path}`, {
    headers,
    signal: AbortSignal.timeout(appConfig.api.timeout),
  });
  const rawResponseText = await res.text();

  if (!res.ok) {
    let body: unknown;
    try {
      body = JSON.parse(rawResponseText) as unknown;
    } catch {
      body = { detail: rawResponseText || `HTTP ${res.status}` };
    }
    const msg = formatApiErrorBody(body) || `API error: ${res.status}`;
    const error = new Error(msg);
    (error as ApiResponseError).statusCode = res.status;
    (error as ApiResponseError).code =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : "unknown";
    trackException(error, {
      endpoint: path,
      statusCode: res.status,
      errorCode: (error as ApiResponseError).code,
      method: "GET",
    });
    throw error;
  }

  let data: DqxContractRulesResponse;
  try {
    data = JSON.parse(rawResponseText) as DqxContractRulesResponse;
  } catch (e) {
    const error = new Error(
      `contract-rules response was not valid JSON: ${e instanceof Error ? e.message : String(e)}`
    );
    (error as ApiResponseError).statusCode = res.status;
    throw error;
  }
  return data;
}

export { apiFetch };
